import React, { useContext, useState, useEffect } from 'react';
import { Block } from '../../../../../../types/shared/blocks';
import { DeckContext } from '../../context/DeckContext';
import getParentBlock from '../../../../../../utils/getParentBlock';
import { toastError } from '../../../../../../Toaster/Toast';
import BlockType from '../../../../../../enums/blockType';
import {
	MAX_DECK_DEPTH,
	MAX_DECK_HEIGHT,
	MAX_DECK_WIDTH,
	MIN_STAIR_WIDTH,
} from '../../../../../../constants/limits';
import updateChildBlocks from '../../utils/updateChildBlocks';
import {
	Button,
	IconButton,
	Typography,
	DialogTitle,
	DialogContent,
	DialogActions,
	useTheme,
	Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LandingPopper from './LandingModalV2';
import DeckboxPopper from './DeckboxModalV2';
import StairPopper from './StairModalV2';
import BumpoutPopper from './BumpoutModalV2';
import { Modal } from 'src/globalComponents/modal/Modal';
import ArrowPopper from './ArrowPopper';
import { useDeviceDetection } from '../../../../../../utils/deviceDetection';

const BlockModal: React.FC<{ block: Block }> = ({ block }) => {
	const { state, dispatch, transformWrapperRef } = useContext(DeckContext);
	const deviceInfo = useDeviceDetection();
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [open, setOpen] = useState(true);
	const [blockData, setBlockData] = useState(block);
	const theme = useTheme();

	// Use the block's ref as anchorEl for Popper
	const anchorEl = block?.ref?.current;

	const handleCloseDialog = () => {
		dispatch({ type: 'CLOSE_BLOCK_MODAL' });
		dispatch({ type: 'CLEAR_SELECTION' });
		setOpen(false);
	};

	useEffect(() => {
		save();
	}, [blockData]);

	useEffect(() => {
		setOpen(true);
		setBlockData(block);

		// Auto-scroll canvas to ensure block modal is visible
		if (block?.ref?.current) {
			setTimeout(() => {
				const canvasContainer = document.getElementById('main-canvas');
				if (!canvasContainer) return;

				const blockElement = block.ref.current;
				const blockRect = blockElement.getBoundingClientRect();
				const canvasRect = canvasContainer.getBoundingClientRect();

				const MODAL_WIDTH = 250;
				const PADDING = 20;

				let scrollX = canvasContainer.scrollLeft;
				let scrollY = canvasContainer.scrollTop;

				// Block modal always appears to the right
				const modalRight = blockRect.right + MODAL_WIDTH + PADDING;
				if (modalRight > canvasRect.right) {
					scrollX += modalRight - canvasRect.right;
				}

				// Check if block itself is visible vertically
				if (blockRect.bottom > canvasRect.bottom) {
					scrollY += blockRect.bottom - canvasRect.bottom + PADDING;
				}
				if (blockRect.top < canvasRect.top) {
					scrollY -= canvasRect.top - blockRect.top + PADDING;
				}

				// Smooth scroll to position
				canvasContainer.scrollTo({
					left: scrollX,
					top: scrollY,
					behavior: 'smooth',
				});
			}, 50);
		}
	}, [block]);

	const save = () => {
		const { parentBlock } = getParentBlock(state.blocks, blockData.parentBlock);

		// Landing-specific validation
		if (blockData.type === BlockType.LANDING) {
			const correctHeight =
				parentBlock.type === BlockType.STAIR
					? parentBlock.dimensions.endHeight
					: parentBlock.dimensions.height;

			if (blockData.dimensions.height !== correctHeight) {
				toastError({
					title: 'Height is incorrect',
					description: `${blockData.label} height should equal ${parentBlock.label} height`,
				});
				return;
			}

			if (blockData.dimensions.depth < MIN_STAIR_WIDTH) {
				toastError({
					title: 'Depth is incorrect',
					description: `${blockData.label} depth can't be less than ${MIN_STAIR_WIDTH / 12} feet`,
				});
				return;
			}

			if (blockData.dimensions.width < MIN_STAIR_WIDTH) {
				toastError({
					title: 'Width is incorrect',
					description: `${blockData.label} width can't be less than ${MIN_STAIR_WIDTH / 12} feet`,
				});
				return;
			}

			if (blockData.dimensions.width > MAX_DECK_WIDTH) {
				toastError({
					title: 'Error',
					description: `Landing Width can't be more than ${MAX_DECK_WIDTH / 12} feet`,
				});
				return;
			}

			if (blockData.dimensions.depth > MAX_DECK_DEPTH) {
				toastError({
					title: 'Error',
					description: `Landing Depth can't be more than ${MAX_DECK_DEPTH / 12} feet`,
				});
				return;
			}

			if (blockData.dimensions.height > MAX_DECK_HEIGHT) {
				toastError({
					title: 'Error',
					description: `Landing Height can't be more than ${MAX_DECK_HEIGHT / 12} feet`,
				});
				return;
			}
		}

		if (blockData.type === BlockType.DECKBOX) {
			if (blockData.dimensions.width > MAX_DECK_WIDTH) {
				toastError({
					title: 'Error',
					description: `Deckbox Width can't be more than ${MAX_DECK_WIDTH / 12} feet`,
				});
				return;
			}

			if (blockData.dimensions.depth > MAX_DECK_DEPTH) {
				toastError({
					title: 'Error',
					description: `Deckbox Depth can't be more than ${MAX_DECK_DEPTH / 12} feet`,
				});
				return;
			}

			if (blockData.dimensions.height > MAX_DECK_HEIGHT) {
				toastError({
					title: 'Error',
					description: `Deckbox Height can't be more than ${MAX_DECK_HEIGHT / 12} feet`,
				});
				return;
			}
		}

		const updateChildBlocksParams = {
			blocks: state.blocks,
			oldBlock: block,
			updatedBlock: blockData,
			dispatch,
		};

		dispatch({
			type: 'UPDATE_BLOCK',
			payload: blockData,
		});

		updateChildBlocks(updateChildBlocksParams);
	};

	const handleSave = () => {
		save();
		handleCloseDialog();
	};

	return (
		<ArrowPopper
			open={open}
			anchorEl={anchorEl}
			placement="right-start"
			onClickAway={handleCloseDialog}
			width="250px"
			transformWrapperRef={transformWrapperRef}
			enableCanvasPan={true}
			disablePortal={true}
		>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				sx={{
					p: theme.spacing(
						deviceInfo.screenWidth < 1024 ? 0.75 : 1,
						deviceInfo.screenWidth < 1024 ? 1 : 2
					),
					pb: 0,
				}}
			>
				<DialogTitle
					component="div"
					sx={{
						p: 0,
						fontWeight: 600,
						flex: 1,
						mr: 1,
						lineHeight: 1.3,
						whiteSpace: 'normal',
						wordBreak: 'break-word',
						fontSize:
							deviceInfo.screenWidth < 768
								? '0.8rem'
								: deviceInfo.screenWidth < 1024
									? '0.875rem'
									: deviceInfo.screenWidth < 1366
										? '1rem'
										: '1.25rem',
					}}
				>
					Edit {block.label}
				</DialogTitle>
				<IconButton
					onClick={() => setShowDeleteModal(true)}
					aria-label="delete"
					size="small"
					sx={{
						color: theme.palette.error.main,
						flexShrink: 0,
						fontSize: deviceInfo.screenWidth < 1024 ? '1.25rem' : '1.5rem',
					}}
				>
					<DeleteIcon fontSize="inherit" />
				</IconButton>
			</Stack>
			<DialogContent
				sx={{
					p: theme.spacing(1.25, 1.25),
					maxHeight: '60vh',
					overflowY: 'auto',
					'&::-webkit-scrollbar': {
						width: '0.375rem',
					},
					'&::-webkit-scrollbar-track': {
						background: theme.palette.grey[100],
					},
					'&::-webkit-scrollbar-thumb': {
						background: theme.palette.grey[400],
						borderRadius: '0.1875rem',
					},
					'&::-webkit-scrollbar-thumb:hover': {
						background: theme.palette.grey[500],
					},
				}}
			>
				{blockData.type === BlockType.LANDING && (
					<LandingPopper
						block={blockData}
						setExternalBlockData={setBlockData}
					/>
				)}
				{blockData.type === BlockType.DECKBOX && (
					<DeckboxPopper
						externalBlockData={block}
						blockData={blockData}
						setBlockData={setBlockData}
					/>
				)}
				{blockData.type === BlockType.STAIR && (
					<StairPopper
						block={blockData}
						blockData={blockData}
						setBlockData={setBlockData}
					/>
				)}
				{blockData.type === BlockType.BUMPOUT && (
					<BumpoutPopper
						block={blockData}
						setExternalBlockData={setBlockData}
					/>
				)}
			</DialogContent>
			<DialogActions
				sx={{
					borderTop: '1px solid',
					borderColor: theme.palette.divider,
					p: theme.spacing(
						deviceInfo.screenWidth < 1024 ? 0.75 : 1.5,
						deviceInfo.screenWidth < 1024 ? 1 : 2
					),
					gap:
						deviceInfo.screenWidth < 1024
							? theme.spacing(0.75)
							: theme.spacing(1),
				}}
			>
				<Button
					onClick={handleCloseDialog}
					variant="text"
					sx={{
						textTransform: 'none',
						color: 'black',
					}}
				>
					Cancel
				</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					sx={{ textTransform: 'none' }}
				>
					Save
				</Button>
			</DialogActions>

			<Modal
				open={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				modalTitle={
					<Typography variant="h2" fontWeight="bold">
						Confirm Deletion
					</Typography>
				}
				modalBody={
					<Typography color="text.secondary">
						Are you sure you want to delete {blockData.label} and all its
						components?
					</Typography>
				}
				modalFooter={
					<>
						<Button
							variant="text"
							onClick={() => setShowDeleteModal(false)}
							sx={{ fontWeight: theme.typography.fontWeightBold }}
						>
							Cancel
						</Button>
						<Button
							onClick={() => dispatch({ type: 'DELETE_BLOCK', payload: block })}
							variant="contained"
							color="error"
							sx={{ fontWeight: theme.typography.fontWeightBold }}
						>
							Delete
						</Button>
					</>
				}
				minWidth="300px"
			/>
		</ArrowPopper>
	);
};

export default BlockModal;

function getFeetAndInches(inches: number) {
	const feet = Math.floor(inches / 12);
	const rem = inches - feet * 12;

	return { feet, inches: rem };
}
